import { Link } from 'react-router-dom';
import clsx from 'clsx';
import CopyButton from './CopyButton';
import { openExternal } from '../lib/liff';
import { IconCard, IconCheck, IconMessage, IconX, IconClock } from './Icons';
import { Notice, StatusPill, formatNtd, formatDate, formatDateTime, btnOutline } from './ui';

export interface BookingDetail {
  id: string;
  eventId?: string;
  eventTitle: string;
  eventDate?: string;
  status: string;
  paymentStatus: string;
  totalAmount?: number;
  selectedCategoryId?: string;
  createdAt: string;
  lineGroupUrl?: string | null;
  payment?: { bankName?: string; bankCode?: string; accountNumber?: string; accountName?: string } | null;
}

/** Shared body for the success page and 我的報名 detail. */
export default function BookingView({ booking, showEventLink = true }: { booking: BookingDetail; showEventLink?: boolean }) {
  const pending = booking.paymentStatus === 'pending' && booking.status !== 'cancelled';
  const verified = booking.paymentStatus === 'verified';
  const cancelled = booking.status === 'cancelled' || booking.paymentStatus === 'rejected';
  const amount = booking.totalAmount || 0;
  const pay = booking.payment;

  return (
    <div className="flex flex-col gap-4">
      {/* Status card */}
      <div className={clsx('flex items-start gap-3 p-4 rounded-card', pending ? 'bg-warn-soft' : verified ? 'bg-ok-soft' : cancelled ? 'bg-bad-soft' : 'bg-soft')}>
        <span className={clsx('w-10 h-10 shrink-0 rounded-[12px] inline-flex items-center justify-center text-white',
          pending ? 'bg-warn-dot text-[#5B3A00]' : verified ? 'bg-ok-dot' : cancelled ? 'bg-bad-dot' : 'bg-faint')}>
          {pending ? <IconCard size={20} /> : verified ? <IconCheck size={20} /> : cancelled ? <IconX size={20} /> : <IconClock size={20} />}
        </span>
        <div className="flex-1 min-w-0 flex flex-col gap-0.5 leading-snug">
          <span className="text-[16px] font-black">
            {pending ? (amount > 0 ? `接下來：匯款 ${formatNtd(amount)}` : '等待確認') : verified ? '報名完成，活動當天見' : cancelled ? '這筆報名已取消' : '處理中'}
          </span>
          <span className={clsx('text-[13px]', pending ? 'text-warn' : verified ? 'text-ok' : cancelled ? 'text-bad' : 'text-muted')}>
            {pending ? '匯款後請耐心等候，確認結果會在 LINE 群組公布。' : verified ? '記得加入 LINE 群組，集合資訊會在群組通知。' : cancelled ? '如有疑問請在 LINE 群組詢問。' : ''}
          </span>
        </div>
      </div>

      {/* Payment info */}
      {pending && pay?.accountNumber && (
        <section className="flex flex-col gap-3 p-4 rounded-card bg-soft">
          <div className="flex items-center justify-between">
            <span className="text-[13px] font-bold text-muted">匯款帳戶</span>
            <span className="text-[13px] text-muted">{[pay.bankName, pay.bankCode].filter(Boolean).join(' ')}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="tabular text-[19px] font-black break-words min-w-0">{pay.accountNumber}</span>
            <CopyButton value={pay.accountNumber.replace(/-/g, '')} label="複製" className="shrink-0" />
          </div>
          {pay.accountName && <div className="text-[13px] text-muted">戶名 {pay.accountName}</div>}
          <div className="flex items-center justify-between pt-3 border-t border-line">
            <span className="text-[13px] text-muted">應匯金額</span>
            <span className="tabular text-lg font-black">{formatNtd(amount)}</span>
          </div>
        </section>
      )}

      {/* Summary */}
      <dl className="flex flex-col rounded-card border border-line divide-y divide-line-soft text-[14px]">
        <Row label="活動">
          {showEventLink && booking.eventId ? (
            <Link to={`/event/${booking.eventId}`} className="font-bold text-primary">{booking.eventTitle}</Link>
          ) : (
            <span className="font-bold">{booking.eventTitle}</span>
          )}
        </Row>
        <Row label="活動日期"><span className="tabular">{formatDate(booking.eventDate, { year: 'numeric', month: 'numeric', day: 'numeric', weekday: 'short' })}</span></Row>
        {booking.selectedCategoryId && <Row label="組別"><span>{booking.selectedCategoryId}</span></Row>}
        <Row label="金額"><span className="tabular font-black">{formatNtd(amount)}</span></Row>
        <Row label="狀態"><StatusPill status={cancelled ? 'rejected' : booking.paymentStatus} /></Row>
        <Row label="報名編號"><span className="tabular text-muted">{booking.id.slice(0, 12).toUpperCase()}</span></Row>
        <Row label="送出時間"><span className="tabular text-muted">{formatDateTime(booking.createdAt)}</span></Row>
      </dl>

      {/* LINE group */}
      {booking.lineGroupUrl && !cancelled && (
        <button type="button" onClick={() => openExternal(booking.lineGroupUrl!)} className={btnOutline}>
          <IconMessage size={18} />
          加入活動 LINE 群組
        </button>
      )}

      {!booking.lineGroupUrl && !cancelled && (
        <Notice tone="info">活動通知、集合資訊和對帳結果都會在 LINE 群組公布，記得加入。</Notice>
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3">
      <dt className="text-muted shrink-0">{label}</dt>
      <dd className="min-w-0 text-right">{children}</dd>
    </div>
  );
}
